/**
 * Plan 12, Task 7.1 — Board builder: assembles all Plan 12 components.
 *
 * Wires islands, bridges, tile pads, chest, weapons cache, dropped key pool,
 * sky dome, cloud layer, and fog into a single BoardRenderHandle.
 *
 * ── Plan 16 Task 5.1 — Tile-VFX wire-up ──────────────────────────────────────
 *
 * When `config.tileVfx` is supplied, this builder ALSO instantiates the
 * tile-VFX subsystem (11 pools + manager + resolver). The wire-up is fully
 * optional — callers from earlier plans (plain map smoke tests, headless
 * fixtures) can omit `tileVfx` and the board builds exactly as before.
 *
 * F7 WORKAROUND (CRITICAL): tile-bounce is NOT routed through the manager's
 * resolver chain because manager.spawn calls `scene.add(emitter)` after
 * acquire, and the tile-bounce pool's "emitter" is the actual tile pad
 * Object3D — which is already parented to groups.board. Re-parenting to scene
 * root would break the board hierarchy AND break subsequent scene.remove on
 * release. Instead we subscribe a SEPARATE handler to the eventLog that
 * directly invokes `pools.get('tile_bounce')!.acquire(...)` for LandingEvent.
 * The pool's own internal release/dispose path handles cleanup; the borrowed
 * mesh stays parented to its board group. See tile-vfx-manager.ts §F7 note.
 *
 * ── Plan 17 Task 6.1 — Item-VFX wire-up ──────────────────────────────────────
 *
 * When `config.itemVfx` is supplied, this builder ALSO instantiates the
 * item-VFX subsystem (14 pools + manager + resolver). The wire-up is fully
 * optional — callers without `itemVfx` keep working unchanged (backward compat).
 *
 * GSAP REGISTRY DECISION — SEPARATE registry for item-VFX (option b):
 *   When both `tileVfx` AND `itemVfx` are supplied, each gets its own
 *   GsapRegistry instance. This avoids any tileVfx ↔ itemVfx timeline
 *   interference and preserves the existing Plan 16 tileVfx behaviour exactly.
 *   Cost: one extra GsapRegistry allocation (~small object). Benefit: clean
 *   isolation — neither subsystem can interfere with the other's timelines via
 *   the VIRTUAL_DURATION ceiling. Plan-review SW5 demands zero regression
 *   on existing tileVfx behaviour; separate registries is the safer default.
 *
 * NO F7 WORKAROUND for item-VFX:
 *   Unlike tile-bounce (which borrows a board-parented tile pad Object3D),
 *   ALL 14 item pools return emitters that are freshly created per spawn.
 *   manager.spawn → scene.add(emitter) is safe for all 14. No direct-subscription
 *   bypass is needed.
 */

import * as THREE from 'three';
import type { BoardNode } from '../../../shared/board/types';
import type { SceneGroups, BoardRenderHandle } from '../types';
import type { ShaderRegistry } from '../shaders/types';
import type { SkyforgeSchema } from '../maps/schema';
import { buildIslandMesh } from './islands';
import { buildBridges } from './bridges';
import { buildTilePads } from './tile-markers';
import { createChestOverlay } from './chest-overlay';
import { createWeaponsCacheOverlay } from './weapons-cache-overlay';
import {
  getSurfaceYForNode as resolveSurfaceYForNode,
  getTilePadTopYForNode as resolveTilePadTopYForNode,
  getWorldPositionForNode as resolveWorldPositionForNode,
} from './surface-y';
import { isFloatingBoardNode } from '../../../shared/board/snap-rules';
import { createDroppedKeyPool } from './dropped-key';
import { buildSkyDome } from './sky-dome';
import { buildCloudLayer, type CloudLayerConfig } from './cloud-layer';
import { attachFog } from './fog';
import { createGsapRegistry } from '../vfx/gsap-registry';
import type { GsapRegistry } from '../vfx/gsap-registry';
import {
  createTileVfxPools,
  createTileVfxEventResolver,
  createTileVfxManager,
} from '../vfx/tile/tile-vfx-manager';
import {
  createItemVfxPools,
  createItemVfxEventResolver,
  createItemVfxManager,
} from '../vfx/items/item-vfx-manager';
import {
  createEnvCeremonyVfxPools,
  createEnvCeremonyEventResolver,
  createEnvCeremonyVfxManager,
} from '../vfx/env/env-ceremony-manager';
import type { ActiveToxicCloud, GameEvent } from '../../../shared/game/types';
import type { CeremonyEvent } from '../vfx/env/ceremony-events';
import type { EnvCeremonySpawnArgs } from '../vfx/env/env-ceremony-types';
import type { MapHandle } from '../maps';
import type { FogConfig } from './fog';
import type { DaylightSkyConfig } from '../atmosphere/daylight-sky';
import type { AudioSyncHooks } from '../vfx/types';
import { createActiveToxicCloudVisuals } from './active-toxic-clouds';
import { createFloatingTileClouds } from './floating-tile-clouds';

type AmbientEnvPoolId = 'dust_motes' | 'birds';

const AMBIENT_ENV_SEEDS = [
  ['dust_motes', { position: { x: 0, y: 0, z: 0 } }],
  ['birds', {}],
] as const satisfies ReadonlyArray<readonly [AmbientEnvPoolId, EnvCeremonySpawnArgs]>;

export const FLOATING_TILE_HOVER_AMPLITUDE = 0.18;
export const FLOATING_TILE_HOVER_SPEED = 1.8;

/** Plan 16 wire-up config (optional). When omitted, no tile-VFX subsystem
 *  is instantiated and the existing buildBoard() behaviour is unchanged. */
export interface BuildBoardTileVfxConfig {
  readonly eventLog: {
    subscribe: (cb: (e: GameEvent) => void) => string;
    unsubscribe: (id: string) => void;
  };
  readonly getPositionForNode?: (nodeId: string) => { x: number; y: number; z: number } | undefined;
  readonly getConnectionType?: (
    fromNodeId: string,
    toNodeId: string,
  ) => 'walk' | 'jump' | 'teleport' | undefined;
  readonly isVoidNode?: (nodeId: string) => boolean;
  readonly getChestEligibilityThreshold?: () => number;
}

/** Plan 17, Task 6.1 wire-up config (optional). When omitted, no item-VFX subsystem
 *  is instantiated and the existing buildBoard() behaviour is unchanged. */
export interface BuildBoardItemVfxConfig {
  readonly eventLog: {
    subscribe: (cb: (e: GameEvent) => void) => string;
    unsubscribe: (id: string) => void;
  };
  readonly getPositionForPlayer?: (
    playerId: string,
  ) => { x: number; y: number; z: number } | undefined;
  readonly getPositionForNode?: (nodeId: string) => { x: number; y: number; z: number } | undefined;
  readonly getItemModelScene?: (itemId: string) => THREE.Object3D | null;
  readonly getNpcById?: (npcId: string) => import('../../../shared/game/npc/types').Npc | undefined;
  readonly audio?: import('../vfx/types').AudioSyncHooks;
}

/** Plan 18, Task 6.1 wire-up config (optional). When omitted, no env+ceremony VFX subsystem
 *  is instantiated and the existing buildBoard() behaviour is unchanged.
 *
 *  Decoration-mesh callbacks (getLanterns, getCrystals, getFountainAnchors) are
 *  supplied by Plan 19's decoration subsystem. Trophy callback is from Plan 8a.
 *  All are optional — pools are graceful no-ops when callbacks are absent.
 *
 * @example Plan 19 wire-up:
 * ```ts
 * const mapHandle = await loadMap('skyforge', { scene, groups, shaderRegistry, gltfLoader });
 * buildBoard({
 *   ...,
 *   envCeremonyVfx: {
 *     eventLog,
 *     ceremonyBus,
 *     getLanterns: () => mapHandle.getLanterns(),
 *     getCrystals: () => mapHandle.getCrystals(),
 *     getFountainAnchors: () => mapHandle.getFountainAnchors(),
 *   },
 * });
 * ```
 */
export interface BuildBoardEnvCeremonyVfxConfig {
  readonly eventLog: {
    subscribe: (cb: (e: GameEvent) => void) => string;
    unsubscribe: (id: string) => void;
  };
  readonly ceremonyBus: {
    subscribe: (cb: (e: CeremonyEvent) => void) => string;
    unsubscribe: (id: string) => void;
  };
  /** Plan 19 — decoration-mesh callbacks (optional). */
  readonly getLanterns?: () => readonly THREE.Object3D[];
  readonly getCrystals?: () => readonly THREE.Object3D[];
  readonly getFountainAnchors?: () => readonly THREE.Vector3[];
  /** Plan 8a — trophy mesh callback (optional). */
  readonly getTrophyMesh?: () => THREE.Object3D | undefined;
  readonly audio?: import('../vfx/types').AudioSyncHooks;
}

/** Ambient environment VFX can mount during normal board play without
 * ceremony bus plumbing. Ceremony-specific effects remain behind
 * `envCeremonyVfx` for the award/game-over flow. */
export interface BuildBoardAmbientEnvVfxConfig {
  /** Plan 19 — decoration-mesh callbacks (optional). */
  readonly getLanterns?: () => readonly THREE.Object3D[];
  readonly getCrystals?: () => readonly THREE.Object3D[];
  readonly getFountainAnchors?: () => readonly THREE.Vector3[];
  /** Plan 8a — trophy mesh callback (optional). */
  readonly getTrophyMesh?: () => THREE.Object3D | undefined;
}

export interface BuildBoardThemeConfig {
  readonly sky?: DaylightSkyConfig;
  readonly clouds?: CloudLayerConfig;
  /** Legacy gradient colors retained for theme compatibility; analytic sky ignores them. */
  readonly skyTopColor?: THREE.ColorRepresentation;
  readonly skyBottomColor?: THREE.ColorRepresentation;
  readonly fog?: FogConfig;
}

function enableBoardMeshShadows(root: THREE.Object3D): void {
  root.traverse((object) => {
    if (object instanceof THREE.Mesh) {
      object.castShadow = true;
      object.receiveShadow = true;
    }
  });
}

export interface BuildBoardConfig {
  readonly nodes: readonly BoardNode[];
  readonly groups: SceneGroups;
  readonly scene: THREE.Scene;
  readonly shaderRegistry: ShaderRegistry;
  readonly treasureChestNodeId?: string | null;
  readonly weaponsCacheNodeId: string | null;
  /**
   * Named bridge definitions from board.json. When provided, buildBridges uses
   * island-edge endpoints. When absent, no bridges are rendered.
   */
  readonly bridges?: SkyforgeSchema['bridges'];
  /**
   * Optional per-island manual radius overrides from board.json.
   * When absent, island radius is auto-computed from tile count + spread.
   */
  readonly islandOverrides?: SkyforgeSchema['islandOverrides'];
  /** Plan 16, Task 5.1 — optional tile-VFX subsystem wire-up. */
  readonly tileVfx?: BuildBoardTileVfxConfig;
  /** Plan 17, Task 6.1 — optional item-VFX subsystem wire-up. */
  readonly itemVfx?: BuildBoardItemVfxConfig;
  /** Plan 18, Task 6.1 — optional env+ceremony VFX subsystem wire-up. */
  readonly envCeremonyVfx?: BuildBoardEnvCeremonyVfxConfig;
  /** Ambient env VFX for the live board path without ceremony bus dependencies. */
  readonly ambientEnvVfx?: BuildBoardAmbientEnvVfxConfig;
  /** Runtime audio cue adapter shared by item/env VFX configs unless they override it. */
  readonly audio?: AudioSyncHooks;
  /** Map atmosphere values applied to sky and fog. */
  readonly theme?: BuildBoardThemeConfig;
  /** GLB-based bridge model. When provided, clones one per named bridge instead of BoxGeometry. */
  readonly bridgeGltfScene?: THREE.Group | null;
  /** GLB-based large floating chunk. When provided, clones one per non-pathing node. */
  readonly chunkLargeGltfScene?: THREE.Group | null;
  /** GLB-based small floating chunk. Fallback when chunkLargeGltfScene is absent. */
  readonly chunkSmallGltfScene?: THREE.Group | null;
  /** GLB-based dropped key model for floating key piles. */
  readonly droppedKeyModelScene?: THREE.Group | null;
  /** Preloaded tile-icon textures keyed by icon name (e.g. 'hazard', 'soul-anchor').
   *  When provided, buildTilePads renders PNG decals on top of each colored pad. */
  readonly tileIconTextures?: Partial<Record<string, THREE.Texture>>;
  /** Optional shared GLB loader for active chest/cache board-object overlays. */
  readonly gltfLoader?: { load(url: string): Promise<THREE.Group> } | null;
  /** Optional app-level GSAP registry for board-object spawn tweens. */
  readonly gsapRegistry?: GsapRegistry | null;
}

/**
 * Assemble all board rendering components and return a unified handle.
 *
 * Adds:
 *   - islands InstancedMesh → groups.board
 *   - bridge meshes → groups.board
 *   - tile pad meshes → groups.board
 *   - chest/cache overlay group → groups.board child named chest-cache-overlays
 *   - dropped key pool (no initial keys, uses groups.board as parent)
 *   - sky dome mesh → groups.environment
 *   - cloud layer mesh → groups.environment
 *   - fog → scene.fog
 */
export function buildBoard(config: BuildBoardConfig): BoardRenderHandle {
  const {
    nodes,
    groups,
    scene,
    shaderRegistry,
    treasureChestNodeId,
    weaponsCacheNodeId,
    bridges,
    islandOverrides,
    tileVfx,
    itemVfx,
    envCeremonyVfx,
    ambientEnvVfx,
    audio,
    theme,
    bridgeGltfScene,
    chunkLargeGltfScene,
    chunkSmallGltfScene,
    droppedKeyModelScene,
    tileIconTextures,
    gltfLoader,
    gsapRegistry,
  } = config;

  // ── Islands ───────────────────────────────────────────────────────────────
  const islands = buildIslandMesh(nodes, chunkLargeGltfScene, chunkSmallGltfScene, islandOverrides);
  enableBoardMeshShadows(islands.object);
  groups.board.add(islands.object);
  const nodeById = new Map(nodes.map((node) => [node.id, node]));

  // ── Bridges ───────────────────────────────────────────────────────────────
  // Use named bridges array (from boardSchema.bridges[]) with island-edge endpoints.
  // Falls back to empty when no bridges are defined.
  const bridgeResult = buildBridges({
    bridges: bridges ?? [],
    islandData: islands.islandData,
    shaderRegistry,
    bridgeGltfScene,
  });
  const surfaceLookup = {
    islandData: islands.islandData,
    bridgeTransforms: bridgeResult.bridgeTransforms,
  };
  for (const mesh of bridgeResult.meshes) {
    enableBoardMeshShadows(mesh);
    groups.board.add(mesh);
  }

  // ── Tile pads (hex prisms + icon decals) ─────────────────────────────────
  // Pass islandData so hex prisms sit on the grass surface (not at node.position.y).
  const tilePadsResult = buildTilePads({
    nodes,
    islandData: islands.islandData,
    bridgeTransforms: bridgeResult.bridgeTransforms,
    tileIconTextures: tileIconTextures ?? {},
  });
  enableBoardMeshShadows(tilePadsResult.group);
  groups.board.add(tilePadsResult.group);
  const floatingTilePads = nodes
    .filter((node) => node.type !== 'pathing' && isFloatingBoardNode(node))
    .map((node, index) => {
      const mesh = tilePadsResult.getMesh(node.id);
      return mesh != null
        ? {
            mesh,
            baseY: mesh.position.y,
            phase: index * 1.618,
          }
        : null;
    })
    .filter((entry): entry is { mesh: THREE.Mesh; baseY: number; phase: number } => entry != null);
  let floatingTileHoverTime = 0;
  const floatingTileClouds = createFloatingTileClouds({
    nodes,
    parent: groups.board,
    getTileMesh: (nodeId) => tilePadsResult.getMesh(nodeId),
  });

  // ── Active board-object overlays ─────────────────────────────────────────
  const overlayGroup = new THREE.Group();
  overlayGroup.name = 'chest-cache-overlays';
  groups.board.add(overlayGroup);

  const chestOverlay = createChestOverlay({
    parent: overlayGroup,
    surfaceLookup,
    gltfLoader,
    gsapRegistry,
  });
  const cacheOverlay = createWeaponsCacheOverlay({
    parent: overlayGroup,
    surfaceLookup,
    gltfLoader,
    gsapRegistry,
  });

  function setTileVisible(nodeId: string, visible: boolean): void {
    tilePadsResult.setVisible(nodeId, visible);
  }

  function surfaceYForNodeId(nodeId: string): number | null {
    const node = nodeById.get(nodeId);
    return node != null ? resolveSurfaceYForNode(node, surfaceLookup) : null;
  }

  function tilePadTopYForNodeId(nodeId: string): number | null {
    const node = nodeById.get(nodeId);
    return node != null ? resolveTilePadTopYForNode(node, surfaceLookup) : null;
  }

  function worldPositionForNodeId(nodeId: string): THREE.Vector3 | null {
    const node = nodeById.get(nodeId);
    return node != null ? resolveWorldPositionForNode(node, surfaceLookup) : null;
  }

  let hiddenOverlayNodeIds = new Set<string>();
  function syncOverlayTileVisibility(
    nextTreasureChestNodeId: string | null,
    nextWeaponsCacheNodeId: string | null,
  ): void {
    const nextHidden = new Set<string>();
    if (nextTreasureChestNodeId != null && nextTreasureChestNodeId !== '') {
      nextHidden.add(nextTreasureChestNodeId);
    }
    if (nextWeaponsCacheNodeId != null && nextWeaponsCacheNodeId !== '') {
      nextHidden.add(nextWeaponsCacheNodeId);
    }

    for (const nodeId of hiddenOverlayNodeIds) {
      if (!nextHidden.has(nodeId)) {
        setTileVisible(nodeId, true);
      }
    }
    for (const nodeId of nextHidden) {
      if (!hiddenOverlayNodeIds.has(nodeId)) {
        setTileVisible(nodeId, false);
      }
    }
    hiddenOverlayNodeIds = nextHidden;
  }

  const staticMapHandle = { schema: { nodes } } as MapHandle;
  if (treasureChestNodeId != null && treasureChestNodeId !== '') {
    chestOverlay.update(treasureChestNodeId, staticMapHandle);
  }
  if (weaponsCacheNodeId != null && weaponsCacheNodeId !== '') {
    cacheOverlay.update(weaponsCacheNodeId, staticMapHandle);
  }
  syncOverlayTileVisibility(treasureChestNodeId ?? null, weaponsCacheNodeId ?? null);

  // ── Dropped key pool ──────────────────────────────────────────────────────
  // parentGroup is groups.board; pool starts with no active keys.
  const droppedKeyPool = createDroppedKeyPool(groups.board, { modelScene: droppedKeyModelScene });
  const tintedSoulAnchorNodeIds = new Set<string>();

  // ── Sky dome ─────────────────────────────────────────────────────────────
  const skyDome = buildSkyDome(shaderRegistry, theme?.sky);
  groups.environment.add(skyDome.mesh);

  // ── Cloud layer ───────────────────────────────────────────────────────────
  const cloudLayer = buildCloudLayer(shaderRegistry, theme?.clouds);
  groups.environment.add(cloudLayer.mesh);

  // ── Fog ───────────────────────────────────────────────────────────────────
  const fogResult = attachFog(scene, theme?.fog);

  // ── Tile-VFX wire-up (Plan 16, Task 5.1) ──────────────────────────────────
  // Build the GsapRegistry, instantiate all 11 pools, compose the resolver,
  // and construct the manager. Subscribe a SEPARATE handler for tile-bounce
  // (F7 workaround). Spawn one ambient_glow emitter per non-`pathing` tile.
  // The whole block is gated on config.tileVfx — when omitted the board
  // builds exactly as before (Plan 12 behaviour preserved).

  let tileVfxState: {
    registry: ReturnType<typeof createGsapRegistry>;
    pools: ReturnType<typeof createTileVfxPools>;
    manager: ReturnType<typeof createTileVfxManager>;
    tileBounceSubscriberId: string;
  } | null = null;

  if (tileVfx) {
    // Build the nodeId → tile pad reverse map by inverting meshToTileInfo.
    // Coverage: every tile pad mesh yields one entry.
    const reverseMap = new Map<string, THREE.Object3D>();
    for (const [mesh, info] of tilePadsResult.meshToTileInfo.entries()) {
      reverseMap.set(info.nodeId, mesh);
    }

    const registry = createGsapRegistry();
    const pools = createTileVfxPools({ scene, registry });
    const resolver = createTileVfxEventResolver({
      getPositionForNode: tileVfx.getPositionForNode,
      getConnectionType: tileVfx.getConnectionType,
      isVoidNode: tileVfx.isVoidNode,
      getChestEligibilityThreshold: tileVfx.getChestEligibilityThreshold,
    });

    const manager = createTileVfxManager({
      scene,
      eventLog: tileVfx.eventLog,
      pools,
      resolver,
    });

    // ── F7 workaround — direct tile-bounce subscription ──────────────────
    // The composed resolver in createTileVfxEventResolver() OMITS tile_bounce
    // because manager.spawn() would call scene.add(tilePad), re-parenting
    // the pad from groups.board to scene root and breaking the board
    // hierarchy. Instead we subscribe directly to LandingEvent and call
    // pools.get('tile_bounce')!.acquire(...) — which animates the pad
    // in-place without touching its parentage.
    const tileBouncePool = pools.get('tile_bounce');
    const tileBounceSubscriberId = tileVfx.eventLog.subscribe((event: GameEvent) => {
      if (event.type !== 'landing') return;
      if (!tileBouncePool) return;
      const tileMarker = reverseMap.get(event.nodeId);
      if (!tileMarker) return;
      const position = tileVfx.getPositionForNode?.(event.nodeId) ?? { x: 0, y: 0, z: 0 };
      tileBouncePool.acquire({ position, tileMarker });
    });

    // ── Ambient glow seed: one per non-pathing tile ──────────────────────
    // Iterate all nodes and spawn ambient_glow at the tile's world position.
    // Pathing tiles have no pad — skipping them avoids invisible/zero-radius
    // ambient glows in the void between board islands.
    for (const node of nodes) {
      if (node.type === 'pathing') continue;
      manager.spawn('ambient_glow', {
        position: tileVfx.getPositionForNode?.(node.id) ?? node.position,
        tileType: node.type,
      });
    }

    tileVfxState = { registry, pools, manager, tileBounceSubscriberId };
  }

  // ── Item-VFX wire-up (Plan 17, Task 6.1) ─────────────────────────────────
  // Build a SEPARATE GsapRegistry (isolation decision — see leading comment),
  // instantiate all 14 pools, compose the resolver, and construct the manager.
  // The whole block is gated on config.itemVfx — when omitted the board
  // builds exactly as before (Plan 12 + Plan 16 behaviour preserved).
  //
  // NO F7 workaround needed: all 14 item pools spawn freshly-created emitters
  // (not borrowed board-hierarchy objects), so manager.spawn → scene.add is
  // safe for every pool.

  let itemVfxState: {
    registry: ReturnType<typeof createGsapRegistry>;
    pools: ReturnType<typeof createItemVfxPools>;
    manager: ReturnType<typeof createItemVfxManager>;
  } | null = null;

  if (itemVfx) {
    const registry = createGsapRegistry();
    const pools = createItemVfxPools({ scene, registry });
    const resolver = createItemVfxEventResolver({
      getPositionForPlayer: itemVfx.getPositionForPlayer,
      getPositionForNode: itemVfx.getPositionForNode,
      getItemModelScene: itemVfx.getItemModelScene,
      getNpcById: itemVfx.getNpcById,
    });

    const manager = createItemVfxManager({
      scene,
      eventLog: itemVfx.eventLog,
      pools,
      resolver,
      audio: itemVfx.audio ?? audio,
    });

    itemVfxState = { registry, pools, manager };
  }

  const activeToxicCloudVisuals = createActiveToxicCloudVisuals({
    parent: groups.effects,
    getPositionForNode: worldPositionForNodeId,
  });

  // ── Ambient Env VFX wire-up ───────────────────────────────────────────────
  // Mount normal board-play atmosphere without requiring ceremonyBus plumbing.
  // When envCeremonyVfx is present, the legacy combined path below already
  // seeds the same ambient pools, so this split path stays inactive.

  let ambientEnvState: {
    registry: ReturnType<typeof createGsapRegistry>;
    pools: ReturnType<typeof createEnvCeremonyVfxPools>;
  } | null = null;

  if (ambientEnvVfx && !envCeremonyVfx) {
    const registry = createGsapRegistry();
    const pools = createEnvCeremonyVfxPools({
      scene,
      registry,
      getLanterns: ambientEnvVfx.getLanterns,
      getCrystals: ambientEnvVfx.getCrystals,
      getFountainAnchors: ambientEnvVfx.getFountainAnchors,
      getTrophyMesh: ambientEnvVfx.getTrophyMesh,
    });

    for (const [poolId, args] of AMBIENT_ENV_SEEDS) {
      pools.get(poolId)?.acquire(args);
    }

    ambientEnvState = { registry, pools };
  }

  // ── Env+Ceremony VFX wire-up (Plan 18, Task 6.1) ──────────────────────────
  // Build a SEPARATE GsapRegistry (isolation from tile/item registries).
  // Instantiate env/ceremony pools, compose TWO resolvers (default + confetti fan-out),
  // and construct TWO managers (paired fan-out for award_revealed events per
  // Option 1 design in env-ceremony-manager.ts header note).
  //
  // Seed lightweight daylight ambient pools at scene-build time:
  //   dust_motes, birds
  // Decoration-mesh ambient pools (lantern_flickers, crystal_pulses, waterfall_mist)
  //   do NOT need a seed call — they latch on first acquire() via their callbacks.
  //
  // The block is gated on config.envCeremonyVfx — when omitted the board builds
  // exactly as before (Plan 12 + Plan 16 + Plan 17 behaviour preserved).

  let envCeremonyState: {
    registry: ReturnType<typeof createGsapRegistry>;
    pools: ReturnType<typeof createEnvCeremonyVfxPools>;
    manager: ReturnType<typeof createEnvCeremonyVfxManager>;
    // Confetti fan-out manager (subscribes to same ceremonyBus with confetti resolver)
    confettiManager: ReturnType<typeof createEnvCeremonyVfxManager>;
  } | null = null;

  if (envCeremonyVfx) {
    const registry = createGsapRegistry();
    const pools = createEnvCeremonyVfxPools({
      scene,
      registry,
      getLanterns: envCeremonyVfx.getLanterns,
      getCrystals: envCeremonyVfx.getCrystals,
      getFountainAnchors: envCeremonyVfx.getFountainAnchors,
      getTrophyMesh: envCeremonyVfx.getTrophyMesh,
    });

    // Default resolver: award_revealed → awards_spotlight, trophy_shine → trophy_shine,
    // game_over → fireworks, awards_ceremony_start → null
    const resolver = createEnvCeremonyEventResolver({});

    // Confetti fan-out resolver: award_revealed → confetti only
    const confettiResolver = createEnvCeremonyEventResolver({ resolveForConfetti: true });

    // Primary manager handles awards_spotlight, trophy_shine, fireworks
    const manager = createEnvCeremonyVfxManager({
      scene,
      eventLog: envCeremonyVfx.eventLog,
      ceremonyBus: envCeremonyVfx.ceremonyBus,
      pools,
      ceremonyResolver: resolver,
      audio: envCeremonyVfx.audio ?? audio,
    });

    // Confetti fan-out manager shares the same scene + pools + ceremonyBus
    const confettiManager = createEnvCeremonyVfxManager({
      scene,
      eventLog: envCeremonyVfx.eventLog,
      ceremonyBus: envCeremonyVfx.ceremonyBus,
      pools,
      ceremonyResolver: confettiResolver,
      audio: envCeremonyVfx.audio ?? audio,
    });

    // ── Seed ambient pools at scene-build time ───────────────────────────────
    // dust_motes: particle cloud at origin
    manager.spawn('dust_motes', { position: { x: 0, y: 0, z: 0 } });
    // birds: chevron flock — single shared group (idempotent re-acquire)
    manager.spawn('birds', {});

    envCeremonyState = { registry, pools, manager, confettiManager };
  }

  // ── disposed flag for idempotency ─────────────────────────────────────────
  let disposed = false;

  return {
    atmosphere: {
      sky: skyDome,
      dome: skyDome.mesh,
      fog: fogResult.fog,
      cloudLayer,
    },
    chestOverlay,
    cacheOverlay,
    tileMarkers: {
      setVisible: setTileVisible,
      setTint: (nodeId: string, color: number | null) => tilePadsResult.setTint(nodeId, color),
    },
    syncDroppedKeys(
      droppedKeys: ReadonlyMap<
        string,
        readonly import('@shared/game/types').DroppedKeyPile[] | number
      >,
      playerColors: ReadonlyMap<string, number>,
    ): void {
      droppedKeyPool.sync(droppedKeys, playerColors, worldPositionForNodeId, tilePadTopYForNodeId);
    },
    syncDroppedKeysAtNode(
      nodeId: string,
      piles: readonly import('@shared/game/types').DroppedKeyPile[] | number | undefined,
      playerColors: ReadonlyMap<string, number>,
    ): void {
      droppedKeyPool.syncNode(
        nodeId,
        piles,
        playerColors,
        worldPositionForNodeId,
        tilePadTopYForNodeId,
      );
    },
    syncSoulAnchorClaims(claims: ReadonlyMap<string, number>): void {
      for (const nodeId of tintedSoulAnchorNodeIds) {
        if (!claims.has(nodeId)) {
          tilePadsResult.setTint(nodeId, null);
        }
      }
      tintedSoulAnchorNodeIds.clear();
      for (const [nodeId, color] of claims.entries()) {
        tilePadsResult.setTint(nodeId, color);
        tintedSoulAnchorNodeIds.add(nodeId);
      }
    },
    syncActiveClouds(clouds: readonly ActiveToxicCloud[]): void {
      activeToxicCloudVisuals.sync(clouds);
    },
    getSurfaceYForNode: surfaceYForNodeId,
    getTilePadTopYForNode: tilePadTopYForNodeId,
    getWorldPositionForNode: worldPositionForNodeId,

    update(deltaSeconds: number): void {
      floatingTileHoverTime += deltaSeconds;
      for (const floatingTile of floatingTilePads) {
        floatingTile.mesh.position.y =
          floatingTile.baseY +
          Math.sin(floatingTileHoverTime * FLOATING_TILE_HOVER_SPEED + floatingTile.phase) *
            FLOATING_TILE_HOVER_AMPLITUDE;
      }
      floatingTileClouds.update(deltaSeconds);

      bridgeResult.update(deltaSeconds);
      cloudLayer.update(deltaSeconds);
      droppedKeyPool.update(deltaSeconds);
      activeToxicCloudVisuals.update(deltaSeconds);
      if (tileVfxState) {
        tileVfxState.registry.update(deltaSeconds);
        tileVfxState.manager.update(deltaSeconds);
      }
      if (itemVfxState) {
        itemVfxState.registry.update(deltaSeconds);
        itemVfxState.manager.update(deltaSeconds);
      }
      if (ambientEnvState) {
        ambientEnvState.registry.update(deltaSeconds);
        for (const [, pool] of ambientEnvState.pools) {
          if (pool.mode === 'ambient') {
            pool.update(deltaSeconds);
          }
        }
      }
      if (envCeremonyState) {
        envCeremonyState.registry.update(deltaSeconds);
        envCeremonyState.manager.update(deltaSeconds);
        envCeremonyState.confettiManager.update(deltaSeconds);
      }
    },

    dispose(): void {
      if (disposed) return;
      disposed = true;

      // Tear down env+ceremony VFX FIRST (before item-VFX, tile-VFX, and board groups)
      // so its emitters are removed from the scene before anything else is torn down.
      if (envCeremonyState) {
        // Dispose both managers (each unsubscribes from both buses on dispose)
        envCeremonyState.confettiManager.dispose();
        envCeremonyState.manager.dispose();
        // Walk pools for idempotent F4 guarantee (manager.dispose already walked
        // its active emitters, but pools should be disposed once explicitly)
        for (const [, pool] of envCeremonyState.pools) {
          pool.dispose();
        }
        envCeremonyState.registry.dispose();
        envCeremonyState = null;
      }

      // Tear down item-VFX NEXT (before tile-VFX and board groups), so its
      // emitters are removed from the scene before the board groups are cleared.
      if (itemVfxState) {
        itemVfxState.manager.dispose();
        // manager.dispose() already walked its tracked pools and called pool.dispose()
        // on each. Call dispose() on each pool again for idempotency guarantee
        // (pool.dispose is idempotent per F4 contract).
        for (const [, pool] of itemVfxState.pools) {
          pool.dispose();
        }
        itemVfxState.registry.dispose();
        itemVfxState = null;
      }

      if (ambientEnvState) {
        for (const [, pool] of ambientEnvState.pools) {
          pool.dispose();
        }
        ambientEnvState.registry.dispose();
        ambientEnvState = null;
      }

      // Tear down tile-VFX NEXT so its emitters are removed before the
      // board groups are emptied below (the manager calls scene.remove on
      // each emitter; some emitters that were re-parented by manager.spawn
      // into scene root, others left in groups.board (tile-bounce) — order
      // matters: dispose the manager + pools before nuking the board group.
      if (tileVfxState) {
        tileVfx?.eventLog.unsubscribe(tileVfxState.tileBounceSubscriberId);
        tileVfxState.manager.dispose();
        // pool.dispose() is idempotent on each pool; manager.dispose already
        // walked its tracked pools, but tile_bounce isn't manager-tracked
        // (F7), so we dispose every pool again to guarantee F4 (final GPU
        // teardown across the board).
        for (const [, pool] of tileVfxState.pools) {
          pool.dispose();
        }
        tileVfxState.registry.dispose();
        tileVfxState = null;
      }

      chestOverlay.dispose();
      cacheOverlay.dispose();
      activeToxicCloudVisuals.dispose();
      floatingTileClouds.dispose();
      hiddenOverlayNodeIds.clear();

      // Remove all children from groups.board
      while (groups.board.children.length > 0) {
        groups.board.remove(groups.board.children[0]!);
      }

      // Remove all children from groups.environment
      while (groups.environment.children.length > 0) {
        groups.environment.remove(groups.environment.children[0]!);
      }

      // Dispose subsystems
      islands.dispose();
      bridgeResult.dispose();
      tilePadsResult.dispose();
      droppedKeyPool.dispose();
      skyDome.dispose();
      cloudLayer.dispose();
      fogResult.dispose(scene);
    },

    getTileInfo(
      mesh: THREE.Object3D,
    ): { nodeId: string; tileType: import('../../../shared/board/types').TileType } | undefined {
      return tilePadsResult.meshToTileInfo.get(mesh);
    },
  };
}
